import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Icons from 'lucide-react';

const CareersPage = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const positions = [
    'Ayurveda Dietician',
    'Panchkarma Therapist',
    'Neurologist',
    'Cardiologist',
    'Oncologist',
    'Nephrologist',
    'Orthopedician',
    'Gastroenterologist',
    'Opthalmologist',
    'Dentist',
    'Cosmetologist',
    'Other'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsSubmitting(false);
    setSubmitSuccess(true);
    setFormData({
      name: '',
      email: '',
      phone: '',
      position: '',
      experience: '',
      message: '',
    });

    setTimeout(() => setSubmitSuccess(false), 5000);
  };

  const benefits = [
    { icon: Icons.Heart, title: 'Health & Wellness', description: 'Comprehensive health coverage and wellness programs' },
    { icon: Icons.TrendingUp, title: 'Growth Opportunities', description: 'Career advancement and learning programs' },
    { icon: Icons.Users, title: 'Collaborative Culture', description: 'Work with experts in Ayurveda and technology' },
    { icon: Icons.Globe, title: 'Global Impact', description: 'Make a difference in healthcare worldwide' },
  ];

  return (
    <div style={{ background: 'var(--bg-page)', paddingTop: '80px' }}>
      {/* Hero Section */}
      <section
        style={{
          background: '#0A1628',
          padding: '5rem 1.2rem',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background Effects */}
        <div
          style={{
            position: 'absolute',
            top: '10%',
            left: '10%',
            width: '300px',
            height: '300px',
            background: 'radial-gradient(circle, rgba(34, 197, 94, 0.15) 0%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(60px)',
            zIndex: 0,
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: '10%',
            right: '10%',
            width: '250px',
            height: '250px',
            background: 'radial-gradient(circle, rgba(245, 158, 11, 0.15) 0%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(60px)',
            zIndex: 0,
          }}
        />

        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'rgba(34, 197, 94, 0.2)',
              borderRadius: '2rem',
              padding: '0.5rem 1.5rem',
              marginBottom: '2rem',
              border: '1px solid rgba(34, 197, 94, 0.4)',
            }}
          >
            <Icons.Briefcase size={18} color="#22c55e" />
            <span style={{ color: '#22c55e', fontWeight: '600', fontSize: '0.9rem' }}>
              {t('careers.badge')}
            </span>
          </div>

          <h1 className="heading-hero" style={{ marginBottom: '1.5rem', color: 'var(--text-light)' }}>
            {t('careers.title')}
          </h1>
          
          <p
            className="body-large"
            style={{
              maxWidth: '700px',
              margin: '0 auto',
              color: 'rgba(255, 255, 255, 0.8)',
              lineHeight: '1.8',
            }}
          >
            {t('careers.description')}
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section style={{ padding: '4rem 1.2rem', background: 'var(--bg-section)' }}>
        <div className="container">
          <h2 className="heading-2" style={{ textAlign: 'center', marginBottom: '3rem' }}>
            {t('careers.whyWorkWithUs')}
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '1.5rem',
            }}
          >
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div
                  key={index}
                  style={{
                    background: 'rgba(34, 197, 94, 0.1)',
                    borderRadius: '1rem',
                    padding: '1.5rem',
                    border: '1px solid rgba(34, 197, 94, 0.2)',
                    textAlign: 'center',
                  }}
                >
                  <div
                    style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 1rem',
                    }}
                  >
                    <IconComponent size={28} color="white" />
                  </div>
                  <h3 className="heading-3" style={{ marginBottom: '0.5rem', color: 'var(--text-light)' }}>
                    {benefit.title}
                  </h3>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section style={{ padding: '5rem 1.2rem', background: 'var(--bg-page)' }}>
        <div className="container">
          <div style={{ maxWidth: '700px', margin: '0 auto' }}>
            <h2 className="heading-1" style={{ textAlign: 'center', marginBottom: '1rem' }}>
              {t('careers.applyNow')}
            </h2>
            <p className="body-large" style={{ textAlign: 'center', color: 'var(--text-secondary)', marginBottom: '3rem' }}>
              {t('careers.applySubtitle')}
            </p>

            {submitSuccess && (
              <div
                style={{
                  background: 'rgba(34, 197, 94, 0.2)',
                  border: '1px solid rgba(34, 197, 94, 0.5)',
                  borderRadius: '1rem',
                  padding: '1.5rem',
                  marginBottom: '2rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                }}
              >
                <Icons.CheckCircle size={24} color="#22c55e" />
                <div>
                  <p style={{ color: '#22c55e', fontWeight: '600', marginBottom: '0.25rem' }}>
                    Application Submitted Successfully!
                  </p>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    Thank you for your interest. Our HR team will review your application and get back to you soon.
                  </p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
                {/* Name */}
                <div>
                  <label
                    htmlFor="name"
                    style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: 'var(--text-light)',
                    }}
                  >
                    {t('careers.form.fullName')} *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '0.875rem 1rem',
                      borderRadius: '0.5rem',
                      border: '1px solid rgba(34, 197, 94, 0.3)',
                      background: 'rgba(255, 255, 255, 0.05)',
                      color: 'var(--text-light)',
                      fontSize: '1rem',
                      outline: 'none',
                    }}
                    placeholder="Enter your full name"
                  />
                </div>

                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: 'var(--text-light)',
                    }}
                  >
                    {t('careers.form.email')} *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '0.875rem 1rem',
                      borderRadius: '0.5rem',
                      border: '1px solid rgba(34, 197, 94, 0.3)',
                      background: 'rgba(255, 255, 255, 0.05)',
                      color: 'var(--text-light)',
                      fontSize: '1rem',
                      outline: 'none',
                    }}
                    placeholder="Enter your email"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label
                    htmlFor="phone"
                    style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: 'var(--text-light)',
                    }}
                  >
                    {t('careers.form.phone')} *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '0.875rem 1rem',
                      borderRadius: '0.5rem',
                      border: '1px solid rgba(34, 197, 94, 0.3)',
                      background: 'rgba(255, 255, 255, 0.05)',
                      color: 'var(--text-light)',
                      fontSize: '1rem',
                      outline: 'none',
                    }}
                    placeholder="Enter your phone number"
                  />
                </div>

                {/* Position */}
                <div>
                  <label
                    htmlFor="position"
                    style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: 'var(--text-light)',
                    }}
                  >
                    {t('careers.form.position')} *
                  </label>
                  <select
                    id="position"
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '0.875rem 1rem',
                      borderRadius: '0.5rem',
                      border: '1px solid rgba(34, 197, 94, 0.3)',
                      background: 'rgba(255, 255, 255, 0.05)',
                      color: 'var(--text-light)',
                      fontSize: '1rem',
                      outline: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="" style={{ background: '#1a2744' }}>Select a position</option>
                    {positions.map((pos) => (
                      <option key={pos} value={pos} style={{ background: '#1a2744' }}>
                        {pos}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Experience */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label
                    htmlFor="experience"
                    style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: 'var(--text-light)',
                    }}
                  >
                    {t('careers.form.experience')} *
                  </label>
                  <select
                    id="experience"
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '0.875rem 1rem',
                      borderRadius: '0.5rem',
                      border: '1px solid rgba(34, 197, 94, 0.3)',
                      background: 'rgba(255, 255, 255, 0.05)',
                      color: 'var(--text-light)',
                      fontSize: '1rem',
                      outline: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="" style={{ background: '#1a2744' }}>Select experience level</option>
                    <option value="0-1" style={{ background: '#1a2744' }}>0-1 years</option>
                    <option value="1-3" style={{ background: '#1a2744' }}>1-3 years</option>
                    <option value="3-5" style={{ background: '#1a2744' }}>3-5 years</option>
                    <option value="5-10" style={{ background: '#1a2744' }}>5-10 years</option>
                    <option value="10+" style={{ background: '#1a2744' }}>10+ years</option>
                  </select>
                </div>

                {/* Message */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label
                    htmlFor="message"
                    style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '500',
                      color: 'var(--text-light)',
                    }}
                  >
                    {t('careers.form.aboutYourself')} *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    style={{
                      width: '100%',
                      padding: '0.875rem 1rem',
                      borderRadius: '0.5rem',
                      border: '1px solid rgba(34, 197, 94, 0.3)',
                      background: 'rgba(255, 255, 255, 0.05)',
                      color: 'var(--text-light)',
                      fontSize: '1rem',
                      outline: 'none',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                    }}
                    placeholder="Tell us about your background, skills, and why you want to join Ayurmed Global..."
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div style={{ marginTop: '2rem', textAlign: 'center' }}>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary"
                  style={{
                    padding: '1rem 3rem',
                    fontSize: '1rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    opacity: isSubmitting ? 0.7 : 1,
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <Icons.Loader2 size={20} className="animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      {t('careers.form.submit')}
                      <Icons.Send size={18} />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CareersPage;
